import type { NativeAgent } from '@/lib/native-desktop';
import {
  logicalWorkspaceRepositoryFullName,
  logicalWorkspaceRepositoryRemote,
  sourceControlRepositoryFullName,
} from '@/lib/sidebar-threads';
import type { SourceControlRepository } from '@/lib/source-control-connector';
import { resolveSourceControlRepository } from '@/lib/source-control-connector';
import { resolveAgentForRuntime } from './cloud-agents';
import type { SyncedThread, SyncedWorkspace } from './store';
import { sourceWorkspaceForThread, workspaceReplicaForThread } from './workspace-replicas';

export function unavailableConversationWorkspace(
  chatId: string,
  threads: readonly SyncedThread[],
  workspaces: readonly SyncedWorkspace[],
): SyncedWorkspace | null {
  const thread = threads.find((candidate) => candidate.id === chatId);
  // Remote conversations run where their server keeps them.
  if (thread?.runtime === 'cloud') return null;
  if (thread === undefined || workspaceReplicaForThread(thread, workspaces) !== undefined)
    return null;
  return sourceWorkspaceForThread(thread, workspaces) ?? null;
}

export function createWorkspaceRecovery(input: {
  chatId: string;
  workspace: SyncedWorkspace | null;
  agents: readonly NativeAgent[];
  agent: NativeAgent | null;
  setWorking: (working: boolean) => void;
  setError: (error: string | null) => void;
  attach: (input: { logicalWorkspaceId: string; name?: string }) => Promise<boolean>;
  clone: (input: {
    logicalWorkspaceId: string;
    repository: SourceControlRepository;
    name?: string;
  }) => Promise<boolean>;
  /** Hands the conversation to Cloud, which checks out `repository` itself. */
  handOffToCloud: (input: {
    repository: SourceControlRepository;
    checkout: { type: 'remote'; ref: string };
    pullRequest: null;
  }) => Promise<void>;
}) {
  const repositoryName =
    sourceControlRepositoryFullName(input.workspace?.rootPath ?? null) ??
    logicalWorkspaceRepositoryFullName(input.workspace?.syncKey);
  // The forge's address when the workspace's sync key names it; its name alone otherwise.
  const repositoryRemote =
    logicalWorkspaceRepositoryRemote(input.workspace?.syncKey) ?? repositoryName;
  const cloudAvailable =
    repositoryName !== null &&
    resolveAgentForRuntime(input.agents, input.agent?.command, 'cloud') !== undefined;

  async function run(action: () => Promise<void>): Promise<void> {
    input.setWorking(true);
    input.setError(null);
    try {
      await action();
    } catch (reason) {
      input.setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      input.setWorking(false);
    }
  }

  return {
    repositoryName,
    cloneable: input.workspace?.logicalWorkspaceId !== undefined && repositoryName !== null,
    cloudAvailable,
    recoverLocally: async () => {
      if (input.workspace?.logicalWorkspaceId === undefined) return;
      await run(async () => {
        await input.attach({
          logicalWorkspaceId: input.workspace?.logicalWorkspaceId as string,
          name: input.workspace?.name,
        });
      });
    },
    cloneLocally: async () => {
      const workspace = input.workspace;
      if (workspace?.logicalWorkspaceId === undefined || !repositoryName) return;
      await run(async () => {
        await input.clone({
          logicalWorkspaceId: workspace.logicalWorkspaceId as string,
          repository: await resolveSourceControlRepository(repositoryRemote ?? repositoryName),
          name: workspace.name,
        });
      });
    },
    continueInCloud: async () => {
      if (!repositoryName || input.workspace === null) return;
      await run(async () => {
        const repository = await resolveSourceControlRepository(repositoryRemote ?? repositoryName);
        // The folder is not on this machine: Cloud starts from the default branch.
        await input.handOffToCloud({
          repository,
          checkout: { type: 'remote', ref: repository.defaultBranch },
          pullRequest: null,
        });
      });
    },
  };
}
